import { FormEvent, MouseEvent } from 'react';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'redux-router';
import { ReduxRouterAction } from 'redux-router/lib/actionCreators';
import {
  checkCode,
  CheckCode as TCheckCode,
  CheckCodeBound as TCheckCodeBound,
  checkDuplicateAndQuarantineDeferred,
  exceptionTypes,
  logException,
  LogException,
  productInfoDeferred,
  sendEmail,
  SendEmail,
  sendSms,
  SendSms,
  userEntersCode,
  UserEntersCode
} from '../actions/Actions';
import { ICode, IProductInfo, IReferer, IUserInfo } from '../CustomTypes';
import { doSpidLoginLinkWithRedirectToStart } from '../fastflowUtils';
import { svgEmail, svgMobile } from '../icons';
import { Button, InputField } from '../input-components/InputComponents';
import Expire from '../layout-components/Expire';
import { sccPulseTrackPageView } from '../PulseUtils';
import { validateCode } from '../validators';
import './CheckCode.scss';

const INVALID_CODE_FORMAT_VALIDATION_ERROR =
  'Vennligst oppgi en gyldig kode med fire tall';
const CODE_DOES_NOT_MATCH_VALIDATION_ERROR =
  'Angitt kode samsvarer ikke med koden som ble sendt til deg';

export interface IDispatchProps {
  logException: LogException;
  userEntersCode: UserEntersCode;
  checkCode: TCheckCode;
  sendSms: SendSms;
  sendEmail: SendEmail;
  push: (...args: Array<object | string>) => ReduxRouterAction;
}

export interface IStateProps {
  __rct?: string;
  productInfo: IProductInfo;
  referer: IReferer;
  userInfo: IUserInfo;
  code: ICode;
  hasPhone: string;
  phoneNumberSentTo: string;
  emailSentTo: string;
  email: string;
}

export interface IProps extends IDispatchProps, IStateProps {}

export interface IState {
  validationMessage: string;
  wrongCode: boolean;
  sentSmsMessageVisible: boolean;
  sentEmailMessageVisible: boolean;
  formSubmitted: boolean;
}

export class CheckCode extends React.Component<IProps, IState> {
  private inputRef: InputField;

  constructor(props: IProps) {
    super(props);
    this.onCodeChange = this.onCodeChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onResendSms = this.onResendSms.bind(this);
    this.onResendEmail = this.onResendEmail.bind(this);
    this.hideMessages = this.hideMessages.bind(this);
    this.state = {
      formSubmitted: false,
      sentEmailMessageVisible: false,
      sentSmsMessageVisible: false,
      validationMessage: INVALID_CODE_FORMAT_VALIDATION_ERROR,
      wrongCode: false
    };
  }

  public componentDidMount() {
    productInfoDeferred.promise.then(product => {
      const { hasPhone } = this.props;
      const channel = hasPhone && hasPhone === 'true' ? 'sms' : 'email';
      sccPulseTrackPageView(product, {}, 'Check code', { channel });
    });
  }

  public componentDidUpdate(
    prevProps: Readonly<IProps>,
    prevState: Readonly<IState>,
    prevContext: any
  ): void {
    this.inputRef.focus();
  }

  public render() {
    const sentCodeBySms = this.props.hasPhone && this.props.hasPhone === 'true';
    const emailSentTo = decodeURIComponent(this.props.emailSentTo);
    const phoneNumberSentTo = decodeURIComponent(this.props.phoneNumberSentTo);
    return (
      <div>
        <div className="checkCode container">
          <h1>
            {sentCodeBySms && svgMobile()}
            {!sentCodeBySms && svgEmail()}
          </h1>
          {sentCodeBySms && (
            <h1>Vi har sendt deg en kode på SMS til {phoneNumberSentTo}</h1>
          )}
          {!sentCodeBySms && (
            <h1>
              Vi har sendt deg en kode på e-post til{' '}
              <span className="coloured">{emailSentTo}</span>
            </h1>
          )}

          <div className="row">
            <div className="col-xs-10 col-xs-offset-1 col-sm-6 col-sm-offset-3">
              <form onSubmit={this.onSubmit}>
                <InputField
                  label={`Tast inn bekreftelseskoden fra ${
                    sentCodeBySms ? 'sms' : 'e-post'
                  }`}
                  type={'tel'}
                  value={this.props.code.code}
                  onChange={this.onCodeChange}
                  initialFocus={true}
                  validationErrorMessage={this.state.validationMessage}
                  validator={validateCode}
                  overrideValidationError={this.state.wrongCode}
                  formSubmitted={this.state.formSubmitted}
                  maxLength={5}
                  ref={c => (this.inputRef = c)}
                />
                <div>
                  {this.state.sentSmsMessageVisible && (
                    <Expire delay={12000}>
                      <div className="alert alert-info">
                        SMS er sendt til {phoneNumberSentTo}. Ikke riktig
                        mobilnummer?{' '}
                        <a href="" onClick={this.login}>
                          Klikk her
                        </a>{' '}
                        for å logge inn
                      </div>
                    </Expire>
                  )}
                </div>
                <div>
                  {this.state.sentEmailMessageVisible && (
                    <Expire delay={12000}>
                      <div className="alert alert-info">
                        E-post er sendt {emailSentTo}. Ikke riktig e-post?{' '}
                        <a href="" onClick={this.login}>
                          Klikk her
                        </a>{' '}
                        for å logge inn
                      </div>
                    </Expire>
                  )}
                </div>
                <div>
                  <Button
                    type="submit"
                    className="mainButton"
                    disabled={!validateCode(this.props.code.code)}
                  >
                    Til betaling
                  </Button>
                </div>
              </form>
            </div>
          </div>

          <div className="bottomLinks">
            <span className="text">
              Ikke riktig {sentCodeBySms ? 'mobilnummer' : 'e-post'}?<br />
              <a href="" onClick={this.login}>
                Klikk for å logge inn
              </a>
            </span>
            <span className="line">
              <hr />
            </span>
            <span className="text">
              Noe galt med koden?
              <br />
              {sentCodeBySms && (
                <a onClick={this.onResendSms}>Send ny kode på SMS</a>
              )}
              {!sentCodeBySms && (
                <a onClick={this.onResendEmail}>Send ny kode</a>
              )}
              <br />
              {sentCodeBySms && (
                <a onClick={this.onResendEmail}>Send ny kode på e-post</a>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  private login = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const { owningClient, productId, spidTeaser } = this.props.productInfo;
    doSpidLoginLinkWithRedirectToStart(
      owningClient,
      productId,
      this.props.userInfo.email,
      spidTeaser
    );
  };

  private onCodeChange(e: React.FormEvent<HTMLInputElement>) {
    this.hideMessages();
    const code = e.currentTarget.value;
    this.props.userEntersCode(code);
    if (validateCode(code)) {
      this.setState({
        validationMessage: INVALID_CODE_FORMAT_VALIDATION_ERROR,
        wrongCode: false
      });
    }
  }

  private onResendSms() {
    this.hideMessages();
    this.props.sendSms(this.props.email);
    this.setState({
      formSubmitted: false,
      sentSmsMessageVisible: true,
      wrongCode: false
    });
    this.props.userEntersCode('');
    this.inputRef.focus();
  }

  private onResendEmail() {
    this.hideMessages();
    this.props.sendEmail(this.props.email);
    this.setState({
      formSubmitted: false,
      sentEmailMessageVisible: true,
      wrongCode: false
    });
    this.props.userEntersCode('');
    this.inputRef.focus();
  }

  private hideMessages() {
    this.setState({
      sentEmailMessageVisible: false,
      sentSmsMessageVisible: false
    });
  }

  private onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    this.hideMessages();
    this.setState({ formSubmitted: true });

    if (!validateCode(this.props.code.code)) {
      return false;
    }

    const logException = this.props.logException;

    ((this.props.checkCode as any) as TCheckCodeBound)(
      this.props.code.code,
      decodeURIComponent(this.props.email)
    )
      .then((checkCode: { city?: string; zipCode?: string }) => {
        const { productId, pureDigital } = this.props.productInfo;
        const { spidId, sig } = this.props.userInfo;

        let nextPage: string | undefined;
        if (pureDigital) {
          nextPage = 'selectpaymentmethod';
        } else {
          nextPage =
            checkCode.zipCode && checkCode.city ? 'userinfo' : 'address';
        }
        let rctParam = '';
        if (this.props.__rct) {
          rctParam += `&__rct=${this.props.__rct}`;
        }
        checkDuplicateAndQuarantineDeferred.promise
          .then(duplicateRes => {
            if (
              duplicateRes.isDuplicate === true ||
              duplicateRes.isQuarantine === true
            ) {
              // Do nothing. We are sent to duplicate/quarantine page
            } else {
              this.props.push(
                `/products/${productId}/${nextPage}?spidId=${spidId}&sig=${sig}${rctParam}`
              );
            }
          })
          .catch(e => {
            this.props.push(
              `/products/${productId}/${nextPage}?spidId=${spidId}&sig=${sig}${rctParam}`
            );
            logException(exceptionTypes.EXCEPTION, e);
          });
      })
      .catch((e: Error) => {
        logException(exceptionTypes.EXCEPTION, e);
        this.setState({
          validationMessage: CODE_DOES_NOT_MATCH_VALIDATION_ERROR,
          wrongCode: true
        });
      });
  }
}

export default connect<IStateProps, IDispatchProps, {}>(
  state => ({
    __rct: state.router.location.query.__rct,
    code: state.code,
    email: state.router.location.query.email,
    emailSentTo: state.router.location.query.emailSentTo,
    exceptions: state.exceptions,
    hasPhone: state.router.location.query.hasPhone,
    phoneNumberSentTo: state.router.location.query.phoneNumberSentTo,
    productInfo: state.productInfo,
    referer: state.referer,
    userInfo: state.userInfo
  }),
  dispatch =>
    bindActionCreators(
      { logException, userEntersCode, checkCode, sendSms, sendEmail, push },
      dispatch
    )
)(CheckCode);



// WEBPACK FOOTER //
// ./src/page-components/CheckCode.tsx